import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Plus, MagnifyingGlass, X, PencilSimple, Trash, FileArrowUp, UsersThree } from "@phosphor-icons/react";
import HrEmployeeFormDrawer from "@/components/HrEmployeeFormDrawer";
import BranchFilter from "@/components/BranchFilter";
import { useAuth } from "@/context/AuthContext";

const STATUS_TABS = [
  { v: "", label: "All" },
  { v: "active", label: "Active" },
  { v: "on_leave", label: "On Leave" },
  { v: "resigned", label: "Resigned" },
  { v: "terminated", label: "Terminated" },
];

function StatusBadge({ value }) {
  const styles = {
    active: "border-[#00B341] text-[#00B341]",
    on_leave: "border-amber-500 text-amber-600",
    resigned: "border-ink-muted text-ink-muted",
    terminated: "border-destructive text-destructive",
  };
  return (
    <span className={`inline-block px-2 py-0.5 border text-[10px] font-mono uppercase tracking-wider ${styles[value] || "border-canvas-line"}`}>
      {(value || "").replace("_", " ")}
    </span>
  );
}

function EmployeeRow({ employee, branchMap, onEdit, onDelete, canViewSalary }) {
  return (
    <tr className="border-b border-canvas-line hover:bg-canvas-panel/50" data-testid={`hr-emp-row-${employee.id}`}>
      <td className="px-3 py-2 font-mono text-[11px] text-ink">{employee.employee_code}</td>
      <td className="px-3 py-2">
        <div className="text-sm font-semibold text-ink">{employee.name}</div>
        <div className="font-mono text-[10px] text-ink-muted">{employee.phone || "—"}</div>
      </td>
      <td className="px-3 py-2 text-sm text-ink">{employee.designation || "—"}</td>
      <td className="px-3 py-2 text-sm text-ink">{employee.work_area || "—"}</td>
      <td className="px-3 py-2 text-sm text-ink">{branchMap[employee.branch_id] || "—"}</td>
      {canViewSalary && (
        <td className="px-3 py-2 font-mono text-sm text-ink tabular-nums" data-testid={`hr-emp-salary-${employee.id}`}>
          {employee.gross_salary ? `₹${Number(employee.gross_salary).toLocaleString()}` : "—"}
        </td>
      )}
      <td className="px-3 py-2">
        <StatusBadge value={employee.status} />
      </td>
      <td className="px-3 py-2 font-mono text-[11px] text-ink-muted">{employee.date_of_joining || "—"}</td>
      <td className="px-3 py-2 text-right">
        <div className="flex items-center justify-end gap-1">
          <button
            type="button"
            onClick={() => onEdit(employee)}
            data-testid={`hr-emp-edit-${employee.id}`}
            className="p-1.5 border border-canvas-line hover:border-ink"
            title="Edit"
          >
            <PencilSimple size={12} weight="bold" />
          </button>
          <button
            type="button"
            onClick={() => onDelete(employee)}
            data-testid={`hr-emp-delete-${employee.id}`}
            className="p-1.5 border border-canvas-line hover:border-destructive hover:text-destructive"
            title="Delete (hard)"
          >
            <Trash size={12} weight="bold" />
          </button>
        </div>
      </td>
    </tr>
  );
}

function CsvImportDialog({ open, onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [dryResult, setDryResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const reset = () => { setFile(null); setDryResult(null); };
  const closeAndReset = () => { reset(); onClose(); };

  const toB64 = (f) => new Promise((res, rej) => {
    const reader = new FileReader();
    reader.onload = () => res(reader.result.split(",")[1] || "");
    reader.onerror = rej;
    reader.readAsDataURL(f);
  });

  const run = async (dry_run) => {
    if (!file) { toast.error("Select a CSV file first"); return; }
    setLoading(true);
    try {
      const csv_b64 = await toB64(file);
      const { data } = await api.post("/hr/employees/csv-import", { csv_b64, dry_run });
      if (dry_run) {
        setDryResult(data);
      } else {
        toast.success(`Imported: ${data.created} created + ${data.updated} updated (${data.errors?.length || 0} errors)`);
        onImported();
        closeAndReset();
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-ink/40 flex items-center justify-center p-4" data-testid="hr-csv-import-dialog">
      <div className="bg-canvas border border-canvas-line max-w-lg w-full p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div className="text-sm font-semibold">Import Employees from CSV</div>
          <button onClick={closeAndReset} className="p-1"><X size={14} /></button>
        </div>
        <div className="text-xs text-ink-muted">
          Expected columns: EMPLOYEE_NAME, EMPLOYEE_CODE, DESIGNATION, WORK_AREA, GROSS_SALARY, PF, ESI,
          MODE_OF_PAY, BANK_ACCOUNT, IFSC_CODE, BANK_NAME, DATE_OF_JOINING, DATE_OF_LEAVING, STAUS, H_PAY_BONUS_Y_N,
          DATE_OF_CREDIT, ESI_PF_BRANCH, FROM_228_BANK_AC, FROM_3962_AC. Case-insensitive.
        </div>
        <input
          type="file"
          accept=".csv,text/csv"
          onChange={(e) => { setFile(e.target.files?.[0] || null); setDryResult(null); }}
          data-testid="hr-csv-file-input"
          className="w-full text-sm"
        />
        {dryResult && (
          <div className="border border-canvas-line p-3 text-xs font-mono space-y-1 bg-canvas-panel/40" data-testid="hr-csv-dry-summary">
            <div><b>Created (new):</b> {dryResult.created}</div>
            <div><b>Updated (by employee_code match):</b> {dryResult.updated}</div>
            <div><b>Errors:</b> {dryResult.errors?.length || 0}</div>
            {dryResult.errors?.length > 0 && (
              <details className="mt-1">
                <summary className="cursor-pointer text-destructive">Show first 5 errors</summary>
                <ul className="mt-1 ml-3 list-disc">
                  {dryResult.errors.slice(0, 5).map((e, i) => <li key={i}>Line {e.line}: {e.reason}</li>)}
                </ul>
              </details>
            )}
          </div>
        )}
        <div className="flex items-center justify-end gap-2 pt-2">
          <button onClick={closeAndReset} className="px-4 py-2 border border-canvas-line text-sm" data-testid="hr-csv-cancel">Cancel</button>
          <button
            onClick={() => run(true)}
            disabled={!file || loading}
            data-testid="hr-csv-dryrun"
            className="px-4 py-2 border border-canvas-line text-sm hover:border-ink disabled:opacity-50"
          >
            {loading ? "…" : "Dry-run"}
          </button>
          {dryResult && (
            <button
              onClick={() => run(false)}
              disabled={loading}
              data-testid="hr-csv-execute"
              className="px-4 py-2 bg-ink text-white text-sm hover:bg-brand disabled:opacity-50"
            >
              Import for real
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function HrEmployeesPage() {
  const { user } = useAuth();
  // Salary confidentiality — the Gross column, the drawer's Bank + Payroll
  // tab and the CSV import are hidden unless the caller holds hr.salary_view
  // (admin and payroll viewers pass automatically).
  const canViewSalary = useMemo(() => {
    if (user?.role === "admin") return true;
    const perms = new Set(user?.permissions || []);
    return ["hr.salary_view", "hr.payroll.view", "hr.payroll.manage"].some((p) => perms.has(p));
  }, [user]);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState({ total: 0, by_status: {}, by_work_area: {} });
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [branchId, setBranchId] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showCsv, setShowCsv] = useState(false);
  const [branches, setBranches] = useState([]);
  const PAGE_SIZE = 100;

  const branchMap = useMemo(() => {
    const m = {};
    branches.forEach((b) => { m[b.id] = b.name; });
    return m;
  }, [branches]);

  const buildParams = useCallback(() => {
    const p = {};
    if (search.trim()) p.search = search.trim();
    if (status) p.status = status;
    if (branchId) p.branch_id = branchId;
    return p;
  }, [search, status, branchId]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [empRes, sumRes, brRes] = await Promise.all([
        api.get("/hr/employees", { params: { ...buildParams(), limit: PAGE_SIZE, skip: 0 } }),
        api.get("/hr/employees/summary"),
        api.get("/branches"),
      ]);
      setRows(empRes.data);
      const t = Number(empRes.headers?.["x-total-count"]);
      setTotal(Number.isFinite(t) ? t : empRes.data.length);
      setSummary(sumRes.data);
      setBranches(brRes.data || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  const loadMore = async () => {
    if (loadingMore) return;
    setLoadingMore(true);
    try {
      const res = await api.get("/hr/employees", { params: { ...buildParams(), limit: PAGE_SIZE, skip: rows.length } });
      setRows((prev) => [...prev, ...res.data]);
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => { load(); }, [load]);

  const onDelete = async (emp) => {
    if (!window.confirm(
      `Mark ${emp.name} (${emp.employee_code}) as terminated?\n\n` +
      `They'll disappear from the active roster and daily attendance grid, ` +
      `but their history (past attendance, leave, salary config) stays intact. ` +
      `To restore, open the record and set status back to 'active'.`
    )) return;
    try {
      await api.delete(`/hr/employees/${emp.id}`);
      toast.success(`${emp.name} marked terminated — history preserved`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow="HR / 01"
        title="Employees"
        subtitle="Master list of all dealership staff — sales, service, workshop hands, back-office. Payroll config lives here (used by Phase 3 payroll runs)."
        actions={
          <div className="flex items-center gap-2">
            {canViewSalary && (
              <button
                onClick={() => setShowCsv(true)}
                data-testid="hr-csv-open"
                className="px-3 py-2.5 border border-canvas-line text-sm hover:border-ink flex items-center gap-2"
              >
                <FileArrowUp size={13} weight="bold" />
                Import CSV
              </button>
            )}
            <button
              onClick={() => { setEditing(null); setShowForm(true); }}
              data-testid="hr-add-employee-button"
              className="bg-ink text-white px-4 py-2.5 text-sm font-semibold hover:bg-brand flex items-center gap-2"
            >
              <Plus size={14} weight="bold" />
              Add Employee
            </button>
          </div>
        }
      />

      <div className="px-8 py-6 space-y-4">
        {/* KPI tiles */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="hr-kpis">
          <div className="border-2 border-ink p-3 bg-canvas-card">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">Total</div>
            <div className="font-display text-2xl font-bold tabular-nums">{summary.total}</div>
          </div>
          <div className="border-2 border-[#00B341] p-3 bg-canvas-card">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">Active</div>
            <div className="font-display text-2xl font-bold tabular-nums">{summary.by_status?.active || 0}</div>
          </div>
          <div className="border-2 border-amber-500 p-3 bg-canvas-card">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">On Leave</div>
            <div className="font-display text-2xl font-bold tabular-nums">{summary.by_status?.on_leave || 0}</div>
          </div>
          <div className="border-2 border-canvas-line p-3 bg-canvas-card">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">Resigned</div>
            <div className="font-display text-2xl font-bold tabular-nums">{summary.by_status?.resigned || 0}</div>
          </div>
          <div className="border-2 border-destructive p-3 bg-canvas-card">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">Terminated</div>
            <div className="font-display text-2xl font-bold tabular-nums">{summary.by_status?.terminated || 0}</div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 flex-wrap">
          <BranchFilter value={branchId} onChange={setBranchId} testId="hr-emp-branch" />
          <div className="flex border border-canvas-line">
            {STATUS_TABS.map((t) => (
              <button
                key={t.v || "all"}
                type="button"
                onClick={() => setStatus(t.v)}
                data-testid={`hr-status-tab-${t.v || "all"}`}
                className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider ${status === t.v ? "bg-ink text-white" : "text-ink hover:bg-canvas-panel"}`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="relative flex-1 max-w-md min-w-[220px]">
            <MagnifyingGlass size={12} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              placeholder="Search name, code, phone, designation…"
              data-testid="hr-emp-search"
              className="w-full pl-8 pr-8 py-1.5 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
            />
            {search && (
              <button onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5" aria-label="Clear">
                <X size={11} weight="bold" />
              </button>
            )}
          </div>
          <button
            onClick={load}
            className="px-3 py-1.5 border border-canvas-line text-xs font-mono uppercase tracking-wider hover:border-ink"
            data-testid="hr-emp-search-go"
          >
            Search
          </button>
        </div>

        {/* Table */}
        <div className="bg-canvas-card border border-canvas-line overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="hr-employees-table">
              <thead>
                <tr className="border-b border-canvas-line bg-canvas-panel">
                  {["Code", "Name / Phone", "Designation", "Work Area", "Branch", ...(canViewSalary ? ["Gross ₹"] : []), "Status", "Joined", ""].map((h) => (
                    <th key={h} className="text-left px-3 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={canViewSalary ? 9 : 8} className="px-4 py-8 text-center font-mono text-xs text-ink-subtle">Loading…</td></tr>
                )}
                {!loading && rows.length === 0 && (
                  <tr>
                    <td colSpan={canViewSalary ? 9 : 8} className="px-4 py-12 text-center">
                      <UsersThree size={24} weight="bold" className="text-ink-subtle mx-auto mb-2" />
                      <div className="font-mono text-xs text-ink-subtle mb-3">No employees match. Import your master CSV or add one manually.</div>
                    </td>
                  </tr>
                )}
                {rows.map((emp) => (
                  <EmployeeRow key={emp.id} employee={emp} branchMap={branchMap} canViewSalary={canViewSalary} onEdit={(e) => { setEditing(e); setShowForm(true); }} onDelete={onDelete} />
                ))}
                {rows.length < total && (
                  <tr>
                    <td colSpan={canViewSalary ? 9 : 8} className="px-4 py-6 text-center bg-canvas-panel/40">
                      <button
                        type="button"
                        onClick={loadMore}
                        disabled={loadingMore}
                        data-testid="hr-emp-load-more"
                        className="px-4 py-2 border border-canvas-line text-xs font-mono uppercase tracking-wider hover:bg-canvas-panel disabled:opacity-50"
                      >
                        {loadingMore ? "Loading…" : `Load more · showing ${rows.length} of ${total}`}
                      </button>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <HrEmployeeFormDrawer
        open={showForm}
        editing={editing}
        canViewSalary={canViewSalary}
        onClose={() => setShowForm(false)}
        onSaved={() => { setShowForm(false); load(); }}
      />
      <CsvImportDialog open={showCsv} onClose={() => setShowCsv(false)} onImported={load} />
    </div>
  );
}
